import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Copy, Check, Percent, DollarSign, Hash, Target } from 'lucide-react';
import { cn } from '../../lib/utils';
import { dataViewApi } from '../../services/dataViewApi';

const FORMAT_ICONS = {
  percent: { icon: Percent, label: 'Percent', color: 'text-blue-600 bg-blue-50' },
  currency: { icon: DollarSign, label: 'Currency', color: 'text-emerald-600 bg-emerald-50' },
  number: { icon: Hash, label: 'Number', color: 'text-amber-600 bg-amber-50' },
};

function FormatBadge({ format }) {
  const config = FORMAT_ICONS[format] || FORMAT_ICONS.number;
  const Icon = config.icon;
  return (
    <span className={cn('inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium', config.color)}>
      <Icon size={9} />
      {config.label}
    </span>
  );
}

function ScopeBadge({ scope }) {
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium text-violet-600 bg-violet-50">
      <Target size={9} />
      {scope || 'global'}
    </span>
  );
}

function CopyableCode({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <span className="inline-flex items-center gap-1 group/copy">
      <code className="text-[10px] font-mono text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded">
        {text}
      </code>
      <button
        onClick={handleCopy}
        className="opacity-0 group-hover/copy:opacity-100 transition-opacity p-0.5 rounded hover:bg-zinc-200 text-zinc-400 hover:text-zinc-600"
        title="Copy measure name"
      >
        {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
      </button>
    </span>
  );
}

function KpiRow({ kpi }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-zinc-100 rounded-md">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-2.5 py-2 flex items-start gap-2 hover:bg-zinc-50/80 transition-colors rounded-md"
      >
        {expanded
          ? <ChevronDown size={12} className="text-zinc-400 mt-0.5 flex-shrink-0" />
          : <ChevronRight size={12} className="text-zinc-400 mt-0.5 flex-shrink-0" />
        }
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-medium text-zinc-800 truncate">{kpi.name}</span>
            <FormatBadge format={kpi.format} />
            {kpi.scope && <ScopeBadge scope={kpi.scope} />}
          </div>
          {kpi.cube_measure_name && (
            <CopyableCode text={kpi.cube_measure_name} />
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-7 pb-2.5 space-y-1.5">
          {kpi.formula && (
            <div>
              <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide">Formula</span>
              <div className="text-[11px] font-mono text-zinc-600 bg-zinc-50 rounded px-2 py-1 mt-0.5 break-all">
                {kpi.formula}
              </div>
            </div>
          )}
          {kpi.description && (
            <div>
              <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide">Description</span>
              <div className="text-[11px] text-zinc-600 mt-0.5">{kpi.description}</div>
            </div>
          )}
          {!kpi.formula && !kpi.description && (
            <div className="text-[10px] text-zinc-400 italic">No additional details</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function KpiPanel() {
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    dataViewApi.listKpis().then((data) => {
      if (!cancelled) {
        setKpis(data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="px-3 py-4 text-center text-[10px] text-zinc-400">
        Loading KPIs...
      </div>
    );
  }

  if (kpis.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-[10px] text-zinc-400">
        No KPIs defined yet.
      </div>
    );
  }

  return (
    <div className="space-y-1 px-2">
      {kpis.map((kpi) => (
        <KpiRow key={kpi.id || kpi.name} kpi={kpi} />
      ))}
    </div>
  );
}
